import { useQuery } from '@apollo/client';
import { Badge, Drawer, Tag } from 'antd';
import React, { useEffect, useState } from 'react';
import {
  DATE_FORMATS,
  FULFILMENT_POSSIBILITY_RETURN_STATUS,
  ORDER_STATUS,
  SORT,
} from '../common/constants';
import { formatDate } from '../common/utils';
import CommonTable from './CommonTable';
import {
  FULFILMENT_POSSIBILITY_ORDERS_VIEW,
  FULFILMENT_POSSIBILITY_ORDERS_VIEW_LIBRARY,
} from './graphql/Queries';

const RETURN_ORDER_ITEM_CREATED_AT_FIELD = 'returnOrderItemCreatedAt';
const EXPECTED_DELIVERY_DATE_FIELD = 'expectedDeliveryDate';

const initialPagination = { current: 1, pageSize: 10 };

const FulfilmentPossibilityDrawer = ({
  visible,
  onClose,
  libraryInventoryId,
  physicallyAvailableStock,
  // When true the drawer shows the orders that this return can fulfil, instead
  // of the upcoming returns that can replenish an inventory item.
  isReturnsOrderView = false,
  // The clicked return order item id, used to scope the returns order view.
  returnOrderItemId,
  // Use the library-side resolver instead of the admin one.
  isLibrary = false,
  isDesktop,
}) => {
  const initialSort = isReturnsOrderView
    ? { field: EXPECTED_DELIVERY_DATE_FIELD, order: SORT?.ASC }
    : { field: RETURN_ORDER_ITEM_CREATED_AT_FIELD, order: SORT?.ASC };

  // Server-side sort defaults to ascending on expected availability date.
  const [sort, setSort] = useState(initialSort);
  const [paginationProp, setPaginationProp] = useState(initialPagination);

  // Reset sort/pagination whenever a new item is opened.
  useEffect(() => {
    setSort(initialSort);
    setPaginationProp(initialPagination);
  }, [libraryInventoryId, returnOrderItemId, isReturnsOrderView]);

  const viewQuery = isLibrary
    ? FULFILMENT_POSSIBILITY_ORDERS_VIEW_LIBRARY
    : FULFILMENT_POSSIBILITY_ORDERS_VIEW;

  const { data, loading } = useQuery(viewQuery, {
    variables: {
      filter: {
        libraryInventoryId,
        ...(isReturnsOrderView
          ? { isReturnsOrderView: true, returnOrderItemId }
          : {}),
        skip: (paginationProp?.current - 1) * paginationProp?.pageSize,
        limit: paginationProp?.pageSize,
      },
      sort,
    },
    skip: !visible || (!libraryInventoryId && !returnOrderItemId),
    fetchPolicy: 'network-only',
  });

  const isInventoryAvailable = (physicallyAvailableStock ?? 0) > 0;

  const handleTableChange = (pagination, _tableFilter, sorter) => {
    setPaginationProp({ ...paginationProp, ...pagination });
    if (sorter?.field === RETURN_ORDER_ITEM_CREATED_AT_FIELD) {
      setSort({
        field: RETURN_ORDER_ITEM_CREATED_AT_FIELD,
        // No sort order (third click) falls back to the default ascending sort.
        order: sorter?.order === 'descend' ? SORT?.DESC : SORT?.ASC,
      });
    } else if (sorter?.field === EXPECTED_DELIVERY_DATE_FIELD) {
      setSort({
        field: EXPECTED_DELIVERY_DATE_FIELD,
        order: sorter?.order === 'descend' ? SORT?.DESC : SORT?.ASC,
      });
    }
  };

  // Columns shown when listing the orders fulfilled by a return.
  const returnsColumns = [
    {
      title: 'Order ID',
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      width: 150,
      ellipsis: true,
      render: (text) => <strong>{text || '-'}</strong>,
    },
    {
      title: 'User Name',
      dataIndex: 'userName',
      key: 'userName',
      width: 180,
      ellipsis: true,
      render: (text) => text || '-',
    },
    {
      title: 'Current Status',
      dataIndex: 'currentStatus',
      key: 'currentStatus',
      width: 180,
      ellipsis: true,
      render: (status) => {
        if (!status) return '-';
        const config = ORDER_STATUS?.[status];
        return <Badge color={config?.color} text={config?.text || status} />;
      },
    },
    {
      title: 'Placed Date',
      dataIndex: 'orderPlacedDate',
      key: 'orderPlacedDate',
      width: 180,
      ellipsis: true,
      render: (text) => (text ? formatDate(text, DATE_FORMATS?.REGULAR) : '-'),
    },
    {
      title: 'Expected Delivery Date',
      dataIndex: 'expectedDeliveryDate',
      key: 'expectedDeliveryDate',
      width: 200,
      ellipsis: true,
      sorter: true,
      sortOrder:
        sort?.field === EXPECTED_DELIVERY_DATE_FIELD
          ? (sort?.order === SORT?.DESC && 'descend') || 'ascend'
          : null,
      render: (text) => (text ? formatDate(text, DATE_FORMATS?.REGULAR) : '-'),
    },
  ];

  // Columns shown when listing the upcoming returns for an inventory item.
  const inventoryColumns = [
    {
      title: 'Return ID',
      dataIndex: 'returnOrderNumber',
      key: 'returnOrderNumber',
      width: 150,
      ellipsis: true,
      render: (text) => <strong>{text || '-'}</strong>,
    },
    {
      title: 'Order ID',
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      width: 150,
      ellipsis: true,
      render: (text) => text || '-',
    },
    {
      title: 'User Name',
      dataIndex: 'userName',
      key: 'userName',
      width: 180,
      ellipsis: true,
      render: (text) => text || '-',
    },
    {
      title: 'Current Status',
      dataIndex: 'currentStatus',
      key: 'currentStatus',
      width: 180,
      ellipsis: true,
      render: (status) => {
        if (!status) return '-';
        const config = FULFILMENT_POSSIBILITY_RETURN_STATUS[status];
        return (
          <Tag color={config?.color || 'default'}>
            {config?.label || status}
          </Tag>
        );
      },
    },
    {
      title: 'Return Date',
      dataIndex: 'returnOrderItemCreatedAt',
      key: 'returnOrderItemCreatedAt',
      width: 200,
      ellipsis: true,
      sorter: true,
      sortOrder:
        sort?.field === RETURN_ORDER_ITEM_CREATED_AT_FIELD
          ? (sort?.order === SORT?.DESC && 'descend') || 'ascend'
          : null,
      render: (text) => (text ? formatDate(text, DATE_FORMATS?.REGULAR) : '-'),
    },
    {
      title: 'Holding Days',
      dataIndex: 'holdingDays',
      key: 'holdingDays',
      width: 120,
      ellipsis: true,
      render: (text) => text ?? '-',
    },
  ];

  const columns = isReturnsOrderView ? returnsColumns : inventoryColumns;
  const dataSource = data?.fulfilmentPossibilityOrdersView?.data || [];
  const totalCount = data?.fulfilmentPossibilityOrdersView?.count || 0;

  return (
    <Drawer
      title={isReturnsOrderView ? 'Order Fulfilment' : 'Fulfilment Possibility'}
      placement="right"
      onClose={onClose}
      open={visible}
      width={!isDesktop ? '100%' : '65%'}
      className="custom-drawer"
      destroyOnClose
    >
      {!isReturnsOrderView && (
        <div style={{ marginBottom: 24 }}>
          <div
            className="font-size-16 font-weight-bold"
            style={{ marginBottom: 12 }}
          >
            Available Inventory
          </div>
          <div className="d-flex" style={{ flexDirection: 'column', gap: 12 }}>
            <div className="d-flex align-center" style={{ gap: 8 }}>
              <span className="font-size-12">
                Current Available Inventory Count :
              </span>
              <strong>{physicallyAvailableStock ?? '-'}</strong>
            </div>
            <div className="d-flex align-center" style={{ gap: 8 }}>
              <span className="font-size-12">Inventory Status :</span>
              <Tag color={isInventoryAvailable ? 'success' : 'error'}>
                {isInventoryAvailable ? 'Available' : 'Not Available'}
              </Tag>
            </div>
          </div>
        </div>
      )}

      <div
        className="font-size-16 font-weight-bold"
        style={{ marginBottom: 12 }}
      >
        {isReturnsOrderView ? 'Orders that can be fulfilled by this return' : 'Upcoming Returns'}
      </div>
      <div className="colored-table">
        <CommonTable
          columns={columns}
          data={dataSource}
          loadingData={loading}
          onChange={handleTableChange}
          paginationConfig={{
            current: paginationProp?.current,
            pageSize: paginationProp?.pageSize,
            total: totalCount,
          }}
          setHeight={isReturnsOrderView ? 320 : 440}
          rowKey={(record) => record?.returnItemId || record?.orderId}
        />
      </div>
    </Drawer>
  );
};

export default FulfilmentPossibilityDrawer;
